import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomerDropdown from "../../../../../components/Shared/CustomerDropdown";
import { Customer } from "../../../../../types/customer";
import { saveTokens } from "../../../../../services/socialTokenService";
import { CognitoAuthService } from "../../../../../services/cognitoService";
import { Platform } from "../../../../../types/Platforms";
import { YouTubeChannel, YouTubeContentType } from "../types/youtube.types";
import ConnectionSuccessAlert from "../../../../../components/Shared/ConnectionSuccessAlert";
import { useAuth } from "../../../../../context/AuthContext";
import { debug } from "../../../../../utils/debugLogger";
interface YouTubeChannelCardProps {
  channel: YouTubeChannel;
  selectedTypes: Platform[];
  onToggleSelection: (channelId: string, platform: Platform) => void;
}

interface ChannelSelectionProps {
  channels: YouTubeChannelCardProps["channel"][];
  accessToken: string | null;
  refreshToken: string | null;
  email: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

const YouTubeChannelCard: React.FC<YouTubeChannelCardProps> = ({
  channel,
  selectedTypes,
  onToggleSelection,
}) => {
  return (
    <div className="p-6 border rounded-lg border-gray-200 bg-white shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start space-x-6">
        {channel.thumbnails?.default?.url && (
          <img
            src={channel.thumbnails.default.url}
            alt={channel.title}
            className="w-24 h-24 rounded-lg object-cover"
          />
        )}
        <div className="flex-1 space-y-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              {channel.title}
            </h3>
            {channel.description && (
              <p className="mt-1 text-sm text-gray-600">
                {channel.description}
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-lg font-semibold text-gray-900">
                {channel.statistics?.subscriberCount || "0"}
              </div>
              <div className="text-sm text-gray-500">Subscribers</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-gray-900">
                {channel.statistics?.viewCount || "0"}
              </div>
              <div className="text-sm text-gray-500">Total Views</div>
            </div>
            <div className="text-center">
              <div className="text-lg font-semibold text-gray-900">
                {channel.statistics?.videoCount || "0"}
              </div>
              <div className="text-sm text-gray-500">Videos</div>
            </div>
          </div>

          <div className="flex space-x-6 pt-4 border-t">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedTypes.includes(Platform.YOUTUBE)}
                onChange={() => onToggleSelection(channel.id, Platform.YOUTUBE)}
                className="h-5 w-5 text-brand-primary rounded border-gray-300 focus:ring-brand-primary"
              />
              <div>
                <span className="font-medium">Regular Videos</span>
                <p className="text-sm text-gray-500">Long-form content</p>
              </div>
            </label>
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedTypes.includes(Platform.YOUTUBE_SHORTS)}
                onChange={() =>
                  onToggleSelection(channel.id, Platform.YOUTUBE_SHORTS)
                }
                className="h-5 w-5 text-red-600 rounded border-gray-300 focus:ring-red-500"
              />
              <div>
                <span className="font-medium">YouTube Shorts</span>
                <p className="text-sm text-gray-500">Short-form content</p>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

const ChannelSelection: React.FC<ChannelSelectionProps> = ({
  channels,
  accessToken,
  refreshToken,
  email,
  onClose,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [selectedContentTypes, setSelectedContentTypes] = useState<
    Record<string, Platform[]>
  >({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionSaved, setConnectionSaved] = useState(false);
  const { idToken } = useAuth(); // Get the ID token from your auth context or hook
  const handleContentTypeToggle = (channelId: string, platform: Platform) => {
    setSelectedContentTypes((prev) => {
      const current = prev[channelId] || [];
      const updated = current.includes(platform)
        ? current.filter((p) => p !== platform)
        : [...current, platform];
      return {
        ...prev,
        [channelId]: updated,
      };
    });
  };

  const handleSaveConnection = async () => {
    if (!selectedCustomer || !refreshToken || !email) {
      setError("Please select a customer and connect to YouTube");
      return;
    }

    const channelsWithContent = Object.entries(selectedContentTypes)
      .filter(([_, platforms]) => platforms.length > 0)
      .map(([channelId, platforms]) => ({
        channel: channels.find((c) => c.id === channelId)!,
        platforms,
      }));

    if (channelsWithContent.length === 0) {
      setError("Please select at least one channel and content type");
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const created_by = CognitoAuthService.getUserEmail(idToken);

      // Create separate profiles for each channel and platform combination
      const profiles = channelsWithContent.flatMap(({ channel, platforms }) =>
        platforms.map((platform) => ({
          id: channel.id,
          token: refreshToken, // Using refreshToken as we know it's not null from the check above
          platform,
          metadata: {
            name: channel.title,
            description: channel.description || "",
            thumbnail_url: channel.thumbnails?.default?.url || "",
            token_type: "refresh_token", // Changed to indicate this is a refresh token
            content_type:
              platform === Platform.YOUTUBE ? "LONG_FORM" : "SHORT_FORM",
            email: email, // Adding email for account identification
          },
        }))
      );

      if (!idToken) {
        throw new Error("Authentication token not found");
      }

      await saveTokens(
        {
          customer_id: selectedCustomer.customer_id,
          platform: Platform.YOUTUBE,
          created_by,
          profiles,
        },
        idToken
      );

      setConnectionSaved(true);
      onSuccess();
    } catch (error: any) {
      debug.error("Error saving connection:", error);
      setError("Failed to save YouTube connection");
    } finally {
      setIsSaving(false);
    }
  };

  if (connectionSaved) {
    return (
      <ConnectionSuccessAlert
        message="YouTube configuration saved successfully!"
        onReturnToConnections={() => navigate("/social-posts?tab=connect")}
        onGoHome={() => navigate("/")}
      />
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto p-4 space-y-6">
      <h2 className="text-2xl font-bold text-center">
        Connect YouTube Channel
      </h2>

      <CustomerDropdown onCustomerSelect={setSelectedCustomer} />

      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">{error}</div>
      )}

      {channels.length > 0 && (
        <div className="space-y-4">
          <div className="bg-brand-primary/10 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-brand-primary mb-2">
              Available YouTube Channels
            </h3>
            <p className="text-sm text-brand-primary">
              Select the channels you want to connect to Daisy AI
            </p>
          </div>

          {channels.map((channel) => (
            <YouTubeChannelCard
              key={channel.id}
              channel={channel}
              selectedTypes={selectedContentTypes[channel.id] || []}
              onToggleSelection={handleContentTypeToggle}
            />
          ))}

          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">
              Selected channels: {Object.keys(selectedContentTypes).length} of{" "}
              {channels.length}
            </p>
            <p className="text-sm text-gray-600">
              Content types:
              {Object.values(selectedContentTypes).reduce(
                (count, platforms) => count + platforms.length,
                0
              )}{" "}
              selected
            </p>
          </div>
        </div>
      )}

      <div className="flex space-x-4">
        <button
          onClick={onClose}
          className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSaveConnection}
          disabled={
            !selectedCustomer ||
            Object.keys(selectedContentTypes).length === 0 ||
            isSaving
          }
          className={`
            flex-1 py-2 px-4 rounded-md
            ${
              !selectedCustomer ||
              Object.keys(selectedContentTypes).length === 0
                ? "bg-surface-ui cursor-not-allowed"
                : "bg-brand-primary hover:bg-brand-primary-hover text-white"
            }
          `}
        >
          {isSaving ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-3"></div>
              <span>Saving...</span>
            </div>
          ) : (
            "Save Connection"
          )}
        </button>
      </div>
    </div>
  );
};

export default ChannelSelection;
